import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {isMobile} from 'react-device-detect';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {copyToClipboard} from '@Utils/clipboard.util';
import {noop} from '@Utils/general.util';
import useLongPress from '@Hooks/useLongPress';
import {getAiVideoThumbUrl} from '@Libraries/ai-video.library';
import styles from './generated-video-item.module.scss';
import type {GeneratedVideoItemProps} from './generated-video-item.types';

export function GeneratedVideoItem(props: GeneratedVideoItemProps): ReactElement {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressFillRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);
  const [isPromptCopied, setIsPromptCopied] = useState(false);

  const tickProgress = (): void => {
    const videoEl = videoRef.current;
    const fillEl = progressFillRef.current;
    if (videoEl && fillEl && videoEl.duration) {
      fillEl.style.width = `${(videoEl.currentTime / videoEl.duration) * 100}%`;
    }
    rafIdRef.current = requestAnimationFrame(tickProgress);
  };

  const startProgressTracking = (): void => {
    rafIdRef.current ??= requestAnimationFrame(tickProgress);
  };

  const stopProgressTracking = (): void => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  };

  const onVideoEnded = (): void => {
    stopProgressTracking();
    if (progressFillRef.current) {
      progressFillRef.current.style.width = '100%';
    }
  };

  useEffect(() => {
    return stopProgressTracking;
  }, []);

  useEffect(() => {
    if (props.autoPlayOnMount) {
      const videoEl = videoRef.current;
      if (videoEl) {
        videoEl.muted = true;
        videoEl.play().catch(noop);
      }
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return undefined;
    }
    const onContextMenuNative = (e: Event): void => {
      e.preventDefault();
    };
    el.addEventListener('contextmenu', onContextMenuNative, {capture: true});
    return (): void => {
      el.removeEventListener('contextmenu', onContextMenuNative, {capture: true});
    };
  }, []);

  const onMouseEnter = (): void => {
    const videoEl = videoRef.current;
    if (!videoEl) {
      return;
    }
    videoEl.muted = false;
    videoEl.play().catch(noop);
  };

  const onMouseLeave = (): void => {
    if (isMenuOpen) {
      return;
    }
    const videoEl = videoRef.current;
    if (!videoEl) {
      return;
    }
    videoEl.muted = true;
    videoEl.pause();
  };

  const onFullscreenPreview = (): void => {
    setIsMenuOpen(false);
    props.onFullscreenPreview();
  };

  const longPressTriggeredRef = useRef(false);
  const longPressHandler = useLongPress(() => {
    if (isMobile) {
      longPressTriggeredRef.current = true;
      onFullscreenPreview();
    }
  }, 500);

  const onCopyPrompt = (): void => {
    setIsPromptCopied(true);
    void copyToClipboard(props.item.prompt.text);
  };

  const getMenuAlignment = (): ALIGNMENT_TYPE => {
    const isLandscape = props.item.videoProps.width > props.item.videoProps.height;
    return isLandscape ? ALIGNMENT_TYPE.BOTTOM_END : ALIGNMENT_TYPE.BOTTOM_START;
  };

  const getMenuItems = (): ControlledListItemType[] => {
    return [
      {
        id: 'fullscreen-preview',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: window.i18next.t('pmwjs_fullscreen_preview'),
        icon: 'icon-expand',
        isSelected: false,
        className: 'radius-8',
        onClick: onFullscreenPreview,
      },
      {
        id: 'copy-prompt',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: isPromptCopied ? window.i18next.t('pmwjs_prompt_copied') : window.i18next.t('pmwjs_copy_prompt'),
        icon: isPromptCopied ? 'icon-check' : 'icon-copy',
        isSelected: false,
        className: 'radius-8',
        onClick: onCopyPrompt,
      },
    ];
  };

  return (
    <div
      key={props.item.id}
      ref={containerRef}
      className={`radius-12 _full-height _full-width _position-relative _cursor-pointer ${styles.container} ${isMenuOpen ? styles.menuOpen : ''}`}
      onMouseEnter={onMouseEnter}
      {...longPressHandler}
      onMouseLeave={(): void => {
        longPressHandler.onMouseLeave();
        onMouseLeave();
      }}
      onClickCapture={(e): void => {
        if (longPressTriggeredRef.current) {
          e.preventDefault();
          e.stopPropagation();
          longPressTriggeredRef.current = false;
        }
      }}
      onContextMenu={(e): void => {
        e.preventDefault();
      }}
    >
      <video
        ref={videoRef}
        className={`_full-height _full-width ${styles.thumbnail}`}
        src={getAiVideoThumbUrl(props.item.hashedFilename, 'mp4')}
        poster={getAiVideoThumbUrl(props.item.hashedFilename, 'jpg')}
        muted
        playsInline
        preload="metadata"
        onPlay={startProgressTracking}
        onPause={stopProgressTracking}
        onEnded={onVideoEnded}
        onContextMenu={(e): void => {
          e.preventDefault();
        }}
      />
      <div className={`flex-center ${styles.playIcon}`} aria-hidden="true">
        <Icon icon={'icon-play-video'} size={IconSize.SIZE_ICON_16} type={IconType.NONE} shape={IconShape.NONE} />
      </div>
      <div className={styles.progressBar} aria-hidden="true">
        <div ref={progressFillRef} className={styles.progressFill} />
      </div>
      <div
        className={`${styles.menuAnchor} ${isMenuOpen ? '_d-block' : ''}`}
        onClick={(e): void => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Dropdown
          open={isMenuOpen}
          onOpen={(): void => {
            setIsMenuOpen(true);
          }}
          onClose={(): void => {
            setIsMenuOpen(false);
            setIsPromptCopied(false);
          }}
          doesCloseOnOuterClick
          doCloseOnInnerClick={false}
          selector={<Icon icon="icon-menu-dots" size={IconSize.SIZE_ICON_16} type={IconType.SECONDARY} shape={IconShape.CIRCLE} />}
          popup={<ControlledList className="spacing-m-0" items={getMenuItems()} />}
          alignment={getMenuAlignment()}
          position={DROPDOWN_POSITION.FIXED}
          popupOffset={8}
          popUpHasCustomWidth
        />
      </div>
    </div>
  );
}
